import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * Path Finder — vue React staircase + debrief (cyanotype Wave M2-game-chrome).
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig } from './params';
function cellEq(a, b) {
    return a.row === b.row && a.col === b.col;
}
function cellKey(c) {
    return `${c.row}-${c.col}`;
}
function Grid({ puzzle, taps, onTap, disabled, feedback }) {
    const cells = [];
    for (let r = 0; r < puzzle.gridSize; r += 1) {
        for (let c = 0; c < puzzle.gridSize; c += 1) {
            cells.push({ row: r, col: c });
        }
    }
    const tappedSet = new Set(taps.map(cellKey));
    const checkpointSet = new Set(puzzle.checkpoints.map(cellKey));
    const last = taps[taps.length - 1];
    return (_jsx("div", { className: "grid w-full max-w-[360px] gap-s1 border border-cyan-border-faint bg-cyan-surface p-s3", style: {
            gridTemplateColumns: `repeat(${puzzle.gridSize}, minmax(0, 1fr))`,
            aspectRatio: '1 / 1',
        }, children: cells.map((cell) => {
            const k = cellKey(cell);
            const isStart = cellEq(cell, puzzle.start);
            const isGoal = cellEq(cell, puzzle.goal);
            const isCheckpoint = checkpointSet.has(k);
            const isTapped = tappedSet.has(k);
            const isLast = last && cellEq(last, cell);
            let stateClass = 'bg-cyan-bg text-cyan-text-secondary';
            if (feedback === 'correct' && isTapped)
                stateClass = 'bg-cyan-core text-cyan-surface';
            else if (feedback === 'incorrect' && isTapped)
                stateClass = 'bg-cyan-media text-cyan-surface';
            else if (isLast)
                stateClass = 'bg-cyan-network text-cyan-surface';
            else if (isTapped)
                stateClass = 'bg-cyan-network/15 text-cyan-text border border-cyan-network/30';
            else if (isStart)
                stateClass = 'bg-cyan-core text-cyan-surface';
            else if (isGoal)
                stateClass = 'bg-cyan-network/15 text-cyan-text border border-cyan-network/30';
            else if (isCheckpoint)
                stateClass = 'bg-cyan-bg border border-cyan-network text-cyan-network';
            return (_jsxs("button", { type: "button", onClick: () => onTap(cell), disabled: disabled, "aria-label": `Cellule ${cell.row + 1}-${cell.col + 1}`, className: [
                    'flex aspect-square items-center justify-center text-[10px]',
                    'transition duration-feedback ease-soft-wind',
                    stateClass,
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-105 active:scale-95',
                ].join(' '), children: [isStart && '◉', isGoal && '★', !isStart && !isGoal && isCheckpoint && '◆'] }, k));
        }) }));
}
export function PFStaircaseView({ adapter, level: _level, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handleTap = useCallback((cell) => adapter.handleTap(cell), [adapter]);
    const handleReset = useCallback(() => adapter.resetPath(), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsxs("div", { className: "flex h-[24px] items-center justify-center text-[12px] text-cyan-text-secondary", children: [adapterState.subPhase === 'pre_puzzle' && _jsx("span", { children: t('path_finder.phases.watching') }), adapterState.subPhase === 'awaiting_input' && adapterState.puzzle && (_jsxs("span", { children: [t('path_finder.trace_label', { count: adapterState.puzzle.checkpoints.length }), ' ', _jsx("span", { className: "font-mono", children: Math.max(0, adapterState.taps.length - 1) }), " /", ' ', _jsx("span", { className: "font-mono", children: Math.round(adapterState.puzzle.optimalLength * adapterState.puzzle.optimalityTolerance) })] })), adapterState.subPhase === 'feedback' && (_jsx("span", { className: [
                            'font-display font-medium',
                            adapterState.feedback === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: adapterState.feedback === 'correct'
                            ? `✓ ${t('path_finder.feedback.correct')}`
                            : `✗ ${t('path_finder.feedback.incorrect')}` }))] }), adapterState.puzzle && (_jsx(Grid, { puzzle: adapterState.puzzle, taps: adapterState.taps, onTap: handleTap, disabled: adapterState.subPhase !== 'awaiting_input', feedback: adapterState.feedback })), adapterState.subPhase === 'awaiting_input' && (_jsx("button", { type: "button", onClick: handleReset, className: "border border-cyan-border-faint px-s4 py-s2 text-[12px] text-cyan-text-secondary hover:border-cyan-border", children: t('path_finder.reset_path') }))] }));
}
function recommendKey(span) {
    if (span !== null && span >= 5) {
        return 'path_finder.debrief.recommend_excellent';
    }
    if (span !== null && span >= 3) {
        return 'path_finder.debrief.recommend_good';
    }
    return 'path_finder.debrief.recommend_keep';
}
export function PFDebriefView({ level: _level, onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const span = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0 ? 0 : Math.round((trialsCorrect / trialsCompleted) * 100);
    const bestSpan = useMemo(() => {
        let best = 0;
        for (const tt of staircase.trialLog) {
            if (tt.response.correct && tt.value > best)
                best = tt.value;
        }
        return Math.round(best);
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const spanLabel = span === null ? '—' : span.toFixed(1).replace(/\.0$/, '');
    const replay = () => window.location.reload();
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('path_finder.debrief.title'), scoreLabel: t('path_finder.debrief.score_label'), scoreValue: spanLabel, equivalentAge: t('path_finder.accuracy_global', { accuracy }), fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
            { label: t('path_finder.debrief.metric_best_k'), value: bestSpan },
        ], recommendation: t(recommendKey(span)), scientificCaveat: t('path_finder.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map